import { Button, Dropdown, Form, Menu, MenuProps, Modal, Select, Space, Tooltip, message } from "antd";
import React, { useCallback, useState } from "react";
import { Color } from '@tiptap/extension-color'
import ListItem from '@tiptap/extension-list-item'
import Link from '@tiptap/extension-link'
import ImageResize from 'tiptap-extension-resize-image';
import TextStyle from '@tiptap/extension-text-style'
import TextAlign from '@tiptap/extension-text-align'
import Underline from '@tiptap/extension-underline'
import { EditorProvider, useCurrentEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { AlignCenterOutlined, AlignLeftOutlined, AlignRightOutlined, UnderlineOutlined, BoldOutlined, FileImageFilled, FileImageOutlined, FunctionOutlined, GlobalOutlined, ItalicOutlined, MinusOutlined, MoreOutlined, OrderedListOutlined, UnorderedListOutlined } from "@ant-design/icons";
import { BookyButton } from "../Buttons/BookyButton";
import { TextService } from "../../Services/TextService";
import ImageSelect, { getVersionedImageUri } from "../Select/ImageSelect";
import { BlobDto } from "../../Repository/eventbookyapi";
import { DocumentService } from "../../Services/ImageService";
import UploadImage, { UploadImageType } from "../Upload/UploadImage";
import { useAuth } from "../../Contexts/AuthContext";
import { SubscriptionLevel } from "../../Types/SubscriptionLevel";

// Toolbar button with a hover explanation (also used as the accessible name, since most are icon-only).
const ToolbarButton = (props: { title: string, icon?: React.ReactNode, text?: string, onClick: () => void }) =>
  <Tooltip title={props.title}>
    <Button style={{ width: "50px" }} type="default" icon={props.icon} aria-label={props.title} onClick={props.onClick}>{props.text}</Button>
  </Tooltip>;

export const OneFrontHtmlEditor = (props: any) => {
  const [html, setHtml] = useState("" as string);
  let { editor } = useCurrentEditor();
  const GetText = TextService.GetText;
  const { user } = useAuth();


  const MenuBar = (props: any) => {
    const { editor } = useCurrentEditor();

    const [isModalOpen, setIsModalOpen] = useState(false as boolean);
    const [imageSelect, setImageSelect] = useState("" as string);
    const [images, setImages] = useState<BlobDto[]>([]);



    const loadImages = async () => {
      const images = await DocumentService.GetPublicImages();
      setImages(images);
      return images;
    };

    // Same cap as on the picture archive page
    const canUploadMoreImages = !DocumentService.IsPictureArchiveFull(images);
    const canUpload = !user?.hasOnlyReadRights && user?.subscriptionLevel != SubscriptionLevel.Free;

    // Uploaded straight from the editor: refresh the list and preselect the new image
    const uploadFinished = async (relativeUri: string) => {
      const images = await loadImages();
      const uploaded = images.find(i => i.relativeUri === relativeUri);
      if (uploaded?.uri) {
        setImageSelect(getVersionedImageUri(uploaded.uri, uploaded.lastModified));
      }
      message.success(GetText("HtmlEditor_ImageUploaded"));
    };

    const setImageWithUrl = useCallback(async (url: string) => {
      editor?.chain().focus().setImage({ src: url }).run()
    }, [editor]);

    const onImageSelect = (image: string) => {
      setImageSelect(image);
    }

    const setLink = useCallback(() => {
      const previousUrl = editor?.getAttributes('link').href
      const url = window.prompt(GetText("HtmlEditorParameter_UrlPopupText"), previousUrl)

      // cancelled
      if (url === null) {
        return
      }

      // empty
      if (url === '') {
        editor?.chain().focus().extendMarkRange('link').unsetLink()
          .run()

        return
      }

      // update link
      editor?.chain().focus().extendMarkRange('link').setLink({ href: url })
        .run()
    }, [editor]);

    // Image 
    const addImage = useCallback(() => {
      const url = window.prompt(GetText("HtmlEditorParameter_UrlImagePopupText"))

      if (url) {
        editor?.chain().focus().setImage({ src: url }).run()
      }
    }, [editor])

    const addImageFromPictureArchive = useCallback(async () => {
      await loadImages();
      setIsModalOpen(true);
    }, [editor])

    if (!editor) {
      return null
    }

    if (!editor) {
      return null
    }

    const styleWidth = {
      width: "50px"
    }

    if (!editor) {
      return null
    }

    const items: MenuProps['items'] = [
      {
        key: GetText("HtmlEditor_MemberId"),
        label: GetText("EditMember_UniqueId")
      },
    ]

    const cleanEmailParam = (value: string) => {
      return value.toLocaleLowerCase().replaceAll("-", "").replaceAll(" ", "");
    }

    const groupMenu = () => {
      return (<Menu>
        <Menu.Item key="1"><BookyButton type="default" style={{ width: "250px" }} onClick={() => editor.commands.insertContent("{{params." + cleanEmailParam(GetText("HtmlEditorParameter_UniqueId")) + "}}")} text={GetText("EditMember_UniqueId")} /></Menu.Item>
        <Menu.Item key="1"><BookyButton type="default" style={{ width: "250px" }} onClick={() => editor.commands.insertContent("{{params." + cleanEmailParam(GetText("HtmlEditorParameter_FirstName")) + "}}")} text={GetText("EditMember_FirstName")} /></Menu.Item>
        <Menu.Item key="1"><BookyButton type="default" style={{ width: "250px" }} onClick={() => editor.commands.insertContent("{{params." + cleanEmailParam(GetText("HtmlEditorParameter_LastName")) + "}}")} text={GetText("EditMember_LastName")} /></Menu.Item>
        <Menu.Item key="1"><BookyButton type="default" style={{ width: "250px" }} onClick={() => editor.commands.insertContent("{{params." + cleanEmailParam(GetText("HtmlEditorParameter_Email")) + "}}")} text={GetText("EditMember_Email")} /></Menu.Item>
        <Menu.Item key="1"><BookyButton type="default" style={{ width: "250px" }} onClick={() => editor.commands.insertContent("{{params." + cleanEmailParam(GetText("HtmlEditorParameter_PhoneNumber")) + "}}")} text={GetText("EditMember_PhoneNumber")} /></Menu.Item>
        <Menu.Item key="1"><BookyButton type="default" style={{ width: "250px" }} onClick={() => editor.commands.insertContent("{{params." + cleanEmailParam(GetText("HtmlEditorParameter_Address")) + "}}")} text={GetText("EditMember_Address")} /></Menu.Item>
        <Menu.Item key="1"><BookyButton type="default" style={{ width: "250px" }} onClick={() => editor.commands.insertContent("{{params." + cleanEmailParam(GetText("HtmlEditorParameter_PostalCode")) + "}}")} text={GetText("EditMember_PostalCode")} /></Menu.Item>
        <Menu.Item key="1"><BookyButton type="default" style={{ width: "250px" }} onClick={() => editor.commands.insertContent("{{params." + cleanEmailParam(GetText("HtmlEditorParameter_PostalArea")) + "}}")} text={GetText("EditMember_PostalArea")} /></Menu.Item>
        <Menu.Item key="1"><BookyButton type="default" style={{ width: "250px" }} onClick={() => editor.commands.insertContent("{{params." + cleanEmailParam(GetText("HtmlEditorParameter_Company")) + "}}")} text={GetText("EditMember_Company")} /></Menu.Item>
        {/* The membership payments mean nothing in e.g. an event invite. */}
        {!props.hidePaymentVariables ? <Menu.Item key="1"><BookyButton type="default" style={{ width: "250px" }} onClick={() => editor.commands.insertContent("{{params." + cleanEmailParam(GetText("HtmlEditorParameter_PayedAmount")) + "}}")} text={GetText("EditMember_PayedAmount")} /></Menu.Item> : ""}
        {!props.hidePaymentVariables ? <Menu.Item key="1"><BookyButton type="default" style={{ width: "250px" }} onClick={() => editor.commands.insertContent("{{params." + cleanEmailParam(GetText("HtmlEditorParameter_PayedAmountLastYear")) + "}}")} text={GetText("EditMember_PayedAmountLastYear")} /></Menu.Item> : ""}
        {props.enablePaymentLink ? <Menu.Item key="1"><BookyButton type="default" style={{ width: "250px" }} onClick={() => editor.commands.insertContent("{{params." + cleanEmailParam(GetText("HtmlEditorParameter_PaymentLink")) + "}}")} text={GetText("EditMember_PaymentLink")} /></Menu.Item> : ""}
        {props.enablePaymentLink ? <Menu.Item key="1"><BookyButton type="default" style={{ width: "250px" }} onClick={() => editor.commands.insertContent("{{params." + cleanEmailParam(GetText("HtmlEditorParameter_PaymentUrlLastYear")) + "}}")} text={GetText("HtmlEditorParameter_PaymentUrlLastYear")} /></Menu.Item> : ""}
        {props.enablePaymentLink ? <Menu.Item key="1"><BookyButton type="default" style={{ width: "250px" }} onClick={() => editor.commands.insertContent("{{params." + cleanEmailParam(GetText("HtmlEditorParameter_PaymentUrlNextYear")) + "}}")} text={GetText("HtmlEditorParameter_PaymentUrlNextYear")} /></Menu.Item> : ""}

      </Menu>);
    }

    return (
      <>
        <ToolbarButton title={GetText("HtmlEditor_TooltipBold")} icon={<BoldOutlined />} onClick={() => editor.chain().focus().toggleBold().run()} />
        <ToolbarButton title={GetText("HtmlEditor_TooltipItalic")} icon={<ItalicOutlined />} onClick={() => editor.chain().focus().toggleItalic().run()} />
        <ToolbarButton title={GetText("HtmlEditor_TooltipUnderline")} icon={<UnderlineOutlined />} onClick={() => editor.chain().focus().toggleUnderline().run()} />
        <ToolbarButton title={GetText("HtmlEditor_TooltipAlignLeft")} icon={<AlignLeftOutlined />} onClick={() => editor.chain().focus().setTextAlign('left').run()} />
        <ToolbarButton title={GetText("HtmlEditor_TooltipAlignCenter")} icon={<AlignCenterOutlined />} onClick={() => editor.chain().focus().setTextAlign('center').run()} />
        <ToolbarButton title={GetText("HtmlEditor_TooltipAlignRight")} icon={<AlignRightOutlined />} onClick={() => editor.chain().focus().setTextAlign('right').run()} />
        <ToolbarButton title={GetText("HtmlEditor_TooltipHeading1")} text="H1" onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} />
        <ToolbarButton title={GetText("HtmlEditor_TooltipHeading2")} text="H2" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} />
        <ToolbarButton title={GetText("HtmlEditor_TooltipHeading3")} text="H3" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} />
        <ToolbarButton title={GetText("HtmlEditor_TooltipOrderedList")} icon={<OrderedListOutlined />} onClick={() => editor.chain().focus().toggleOrderedList().run()} />
        <ToolbarButton title={GetText("HtmlEditor_TooltipBulletList")} icon={<UnorderedListOutlined />} onClick={() => editor.chain().focus().toggleBulletList().run()} />
        <ToolbarButton title={GetText("HtmlEditor_TooltipHorizontalRule")} icon={<MinusOutlined />} onClick={() => editor.chain().focus().setHorizontalRule().run()} />
        <ToolbarButton title={GetText("HtmlEditor_TooltipLink")} icon={<GlobalOutlined />} onClick={setLink} />
        <ToolbarButton title={GetText("HtmlEditor_TooltipImageUrl")} icon={<FileImageOutlined />} onClick={addImage} />
        <ToolbarButton title={GetText("HtmlEditor_TooltipImageArchive")} icon={<FileImageFilled />} onClick={addImageFromPictureArchive} />

        <Modal title={GetText("MyEventDetails_SelectImage")} width={850} open={isModalOpen} okButtonProps={{ disabled: !imageSelect }} onOk={() => {
          setImageWithUrl(imageSelect);
          setIsModalOpen(false)
        }} onCancel={() => { setIsModalOpen(false) }}>
          {canUpload && <div style={{ marginBottom: 16 }}>
            {canUploadMoreImages
              ? <UploadImage crop={false} uploadImageType={UploadImageType.Public} uploadFinish={uploadFinished} existingFileNames={DocumentService.GetFileNames(images)} />
              : <i>{GetText("PictureArchive_MaxSizeAchived")})</i>}
          </div>}
          <ImageSelect images={images} isPublicImage={true} loadImages={loadImages} onImageSelect={onImageSelect} selectedImage={imageSelect} />
        </Modal>


        {props.hideVariablesMenu ? "" : <Dropdown overlay={groupMenu}>
          <Tooltip title={GetText("HtmlEditor_TooltipVariables")}>
            <Button style={styleWidth} type="default" aria-label={GetText("HtmlEditor_TooltipVariables")}>
              <Space>
                <FunctionOutlined />
              </Space>
            </Button>
          </Tooltip>
        </Dropdown>}
      </>
    )
  }

  const onUpdate = (updateEvent: any) => {
    const editor = updateEvent.editor;
    props.onHtmlChange(editor.isEmpty ? "" : editor.getHTML());
  }

  const extensions = [
    Color.configure({ types: [TextStyle.name, ListItem.name] }),
    StarterKit.configure({
      bulletList: {
        keepMarks: true,
        keepAttributes: false,
      },
      orderedList: {
        keepMarks: true,
        keepAttributes: false,
      },
    },

    ),
    Link.configure({
      autolink: false,
      openOnClick: true
    }),
    // ImageResize extends Image under the same node name, so it replaces it - a separate Image entry was
    // silently ignored. Inline so images live inside a paragraph and follow its text alignment (centering
    // via the paragraph is what mail clients, Outlook included, handle reliably).
    ImageResize.configure({ inline: true }),
    Underline,
    // Left is the default and is not written to the html; center/right become an inline text-align style.
    TextAlign.configure({ types: ['heading', 'paragraph'], alignments: ['left', 'center', 'right'] })
  ]


  const rules: any = [];
  if (props.requiredMessage) {
    rules.push({
      required: true,
      message: props.requiredMessage,
    });
  }

  return (<Form.Item style={props.style} label={props.label} name={props.name}
    rules={rules}
  >
    <EditorProvider onUpdate={onUpdate} slotBefore={<MenuBar enablePaymentLink={props.enablePaymentLink} hidePaymentVariables={props.hidePaymentVariables} hideVariablesMenu={props.hideVariablesMenu} />} extensions={extensions} content={props.initialHtml} children={undefined} /></Form.Item>);
}